import { AppSelect } from './ui/AppSelect';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import { X, Mic, Play, Save, Loader2, RefreshCw, Volume2 } from 'lucide-react';
import { viduVoices } from '../constants/voiceData';
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import { runTaskToCompletion } from "../services/taskService";

const AudioGeneratorModal = ({ visible, onClose, initialText, shotId, onSaveSuccess }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [model, setModel] = useState('vidu');
  const [text, setText] = useState(initialText || '');
  const [voiceId, setVoiceId] = useState('');
  const [speed, setSpeed] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  // 初始化
  useEffect(() => {
    if (visible) {
      setText(initialText || '');
      setAudioUrl(null);
      // 默认选中第一个音色
      if (model === 'vidu' && viduVoices.length > 0) setVoiceId(viduVoices[0].value);
    }
  }, [visible, initialText, model]);

  const currentVoices = viduVoices;

  const handleGenerate = async () => {
    if (!text) { toast.warning('请输入台词'); return; }
    if (!voiceId) { toast.warning('请选择音色'); return; }
    setLoading(true);
    setAudioUrl(null);
    try {
      const selectedVoice = (currentVoices || []).find(v => v.value === voiceId);

      const { result: data } = await runTaskToCompletion(
        "TTS_GENERATE",
        {
          model,
          text,
          voiceId,
          language: selectedVoice?.lang || 'zh',
          speed
        },
        {
          source: "tts",
          timeoutMs: 10 * 60 * 1000,
        }
      );
      
      if (data && data.audioUrl) {
        setAudioUrl(data.audioUrl);
      } else {
        toast.error('生成失败', 0);
      }
    } catch (e) {
      console.error(e);
      toast.error('请求异常', 0);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    console.log("1111")
    console.log(audioUrl)
    console.log(shotId)
    if (!audioUrl || !shotId) return;
    try {
      await authenticatedFetch(`${API_HOST}/api/storyboard/update_audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: shotId, audio: audioUrl })
      });
      if (onSaveSuccess) onSaveSuccess(shotId, audioUrl);
      onClose();
    } catch (e) {
      toast.error('保存失败', 0);
    }
  };

  return (
    <Dialog open={visible} onClose={onClose} className="relative z-[100]">
      <DialogBackdrop
        transition
        className="ui-dialog-backdrop fixed inset-0 backdrop-blur-sm"
        style={{ backgroundColor: 'var(--overlay-heavy)' }}
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel transition className="ui-dialog-panel w-full max-w-lg rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3"
          style={{ backgroundColor: 'var(--color-bg-tertiary)', borderBottom: '1px solid var(--color-border-primary)' }}>
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Mic size={16} style={{ color: theme.colors.accentPrimary }} /> AI 配音工作室
          </h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }} className="ui-pressable rounded-md p-1 hover:text-[var(--color-text-primary)]">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col gap-5 overflow-y-auto custom-scrollbar">

          {/* 1. 模型切换 */}
          <div className="flex p-1 rounded-lg"
            style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
            <button
              onClick={() => setModel('vidu')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                model === 'vidu'
                  ? 'shadow'
                  : ''
              }`}
              style={{
                backgroundColor: model === 'vidu' ? 'var(--color-bg-tertiary)' : 'transparent',
                color: model === 'vidu' ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
              }}
            >
              Vidu 配音 (V2)
            </button>
          </div>

          {/* 2. 文本输入 */}
          <div className="space-y-2">
            <label className="text-2xs uppercase font-bold" style={{ color: 'var(--color-text-muted)' }}>台词文本</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full h-24 rounded-lg p-3 text-sm outline-none resize-none transition-all duration-200"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                border: `1px solid var(--color-border-primary)`,
                color: 'var(--color-text-primary)'
              }}
              placeholder="请输入要合成的台词..."
            />
          </div>

          {/* 3. 参数配置 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-2xs uppercase font-bold" style={{ color: 'var(--color-text-muted)' }}>选择音色</label>
              <AppSelect
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="w-full rounded-lg h-9 px-2 text-xs outline-none cursor-pointer"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: `1px solid var(--color-border-primary)`,
                  color: 'var(--color-text-primary)',
                  appearance: 'none'
                }}
              >
                {currentVoices.map(v => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </AppSelect>
            </div>
            <div className="space-y-2">
              <label className="text-2xs uppercase font-bold flex justify-between" style={{ color: 'var(--color-text-muted)' }}>
                <span>语速</span>
                <span style={{ color: theme.colors.accentPrimary }}>{speed.toFixed(1)}x</span>
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                className="w-full h-1.5 rounded-lg cursor-pointer"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  accentColor: theme.colors.accentPrimary
                }}
              />
            </div>
          </div>

          {/* 4. 试听与操作区 */}
          {audioUrl ? (
            <div className="rounded-xl p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-200"
              style={{ backgroundColor: `${theme.colors.accentPrimary}15`, border: `1px solid ${theme.colors.accentPrimary}30` }}>
              <div className="flex items-center gap-2 text-xs font-bold" style={{ color: theme.colors.accentSecondary }}>
                <Volume2 size={14} /> 生成成功
              </div>
              <audio controls src={audioUrl} className="w-full h-8 block rounded opacity-90" />

              <div className="flex gap-2 mt-1">
                <button
                  onClick={handleSave}
                  className="flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all duration-200"
                  style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                >
                  <Save size={14} /> 确认使用
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="px-3 rounded-lg flex items-center justify-center transition-all duration-200"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-secondary)',
                    border: `1px solid var(--color-border-primary)`
                  }}
                  title="重新生成"
                >
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
                border: `1px solid var(--color-border-primary)`
              }}
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> 正在合成语音...</>
              ) : (
                <><Play size={16} style={{ color: theme.colors.accentPrimary }} className="fill-current" /> 开始生成语音</>
              )}
            </button>
          )}

        </div>
      </DialogPanel>
      </div>
    </Dialog>
  );
};

export default AudioGeneratorModal;
